<template>
    <div
        ref="canvas"
        class="interactive-canvas"
        @mousedown="handleMouseDown"
        @mouseleave="handleMouseLeave"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @touchstart="handleMouseDown"
        @touchmove="handleMouseMove"
        @touchend="handleMouseUp"
        @touchcancel="handleMouseLeave"
        @wheel="handleWheel"
    >
        <slot name="tooltip" :tooltip="tooltip" />

        <div
            ref="canvasLayers"
            :style="{
                width: calculatedSize.width + 'px',
                height: calculatedSize.height + 'px',
                zIndex: 4,
            }"
            class="interactive-canvas-layers"
        >
            <slot
                name="canvasLayers"
                :aspectRatio="aspectRatio"
                :items="layersShapes"
                :imageSize="calculatedSize"
                :zoom="zoom"
                :handleContextMenuVue="handleContextMenuVue"
                :handleFrameClickVue="handleFrameClickVue"
                :handleMouseDownVue="handleMouseDownVue"
                :handleMouseMoveVue="handleMouseMoveVue"
                :handleMouseOverVue="handleMouseOverVue"
                :handleMouseLeaveVue="handleMouseLeaveVue"
                :handleMouseUpVue="handleMouseUpVue"
            />
        </div>
        <div
            v-if="frame"
            id="frame"
            ref="frame"
            class="interactive-canvas-frame"
            :style="{
                width: calculatedSize.width + 'px',
                height: calculatedSize.height + 'px',
            }"
            @contextmenu.prevent="handleContextMenu"
            @click="handleFrameClick"
        >
            <img
                v-if="currentImageUrl"
                ref="image"
                :src="currentImageUrl"
                :style="{
                    width: calculatedSize.width + 'px',
                    height: calculatedSize.height + 'px',
                }"
                @load="handleImageLoaded"
                @error="handleImageError"
            />
            <div
                ref="svgContainer"
                :style="{
                    width: calculatedSize.width + 'px',
                    height: calculatedSize.height + 'px',
                }"
                class="interactive-canvas-frame-svg"
            />
            <!--        <slot name="overlay"></slot>-->
        </div>
    </div>
</template>

<script>
import { SVGCanvas } from "../scripts/utils/SVGUtils.js";
import PanZoom from "../scripts/tools/PanZoom";
import ToolBaseManager from "../scripts/managers/ToolBaseManager";
import FrameBaseManager from "../scripts/managers/FrameBaseManager";
import TooltipBaseManager from "../scripts/managers/TooltipBaseManager";

export default {
    components: {},
    props: {
        frame: {
            type: Object,
            required: true,
        },
        stretchMode: {
            type: String,
            default: "width", // options could be 'width' or 'height'
        },
        breakpoint: {
            // 1 - MOBILE, 2 - TABLET, 4 - DESKTOP, 7 - ALL
            type: Number,
            default: 7,
        },
        sizeScale: {
            type: Number,
            default: 1,
        },
        maxZoom: {
            type: Number,
            default: 5,
        },
        minZoom: {
            type: Number,
            default: 1,
        },
        crossBorders: {
            type: [Boolean, Number],
            default: false,
        },
        toolManager: {
            default: () => new ToolBaseManager(),
        },
        frameManager: {
            default: () => new FrameBaseManager(),
        },
        tooltipManager: {
            default: () => new TooltipBaseManager(),
        },
        resetOnFrameChange: {
            type: [Boolean, Number],
            default: true,
        },
        defaultSize: {
            type: Object,
            default: () => {
                return {
                    width: null,
                    height: null,
                };
            },
        },
    },
    data() {
        return {
            tooltipManagerInstance: null,
            zoom: 1,
            startTouches: null,
            mounted: false,
            hoveredLayer: null,
            currentImageUrl: null,
            currentImageLoad: null,
            calculatedSize: {
                width: 0,
                height: 0,
            },
            naturalSize: {
                width: 0,
                height: 0,
            },
            aspectRatio: 1,

            svg: null,
            layersShapes: {},
        };
    },
    watch: {
        frame: {
            handler(newFrame) {
                if (this.resetOnFrameChange) {
                    this.toolManager.panZoom.reset();
                }
                this.$nextTick(async () => {
                    this.loadImage(newFrame);
                });
            },
        },
        breakpoint: {
            handler(newBreakpoint, oldBreakpoint) {
                this.frame.layers.forEach((layer) => {
                    if (layer.type == "polygon") {
                        layer.tool.applyLayerStyle(
                            layer.shape,
                            layer.options[layer.state.value],
                            layer.visible
                        );
                    }
                });
            },
        },
    },

    mounted() {
        const panZoom = new PanZoom(
            {
                toolManager: this.toolManager,
                frameManager: this.frameManager,
                tooltipManager: this.tooltipManagerInstance,
                svg: this.svg,
                $refs: {
                    frame: this.$refs.frame,
                    canvas: this.$refs.canvas,
                    svgContainer: this.$refs.svgContainer,
                    canvasLayers: this.$refs.canvasLayers,
                },
                getters: {
                    getBreakpoint: () => {
                        return this.getBreakpoint;
                    },
                    getZoom: () => {
                        return this.getZoom;
                    },
                    getAspectRatio: () => {
                        return this.getAspectRatio;
                    },

                    getNaturalSize: () => {
                        return this.getNaturalSize;
                    },
                    getCalculatedWidth: () => {
                        return this.getCalculatedWidth;
                    },
                    getCalculatedHeight: () => {
                        return this.getCalculatedHeight;
                    },
                },

                functions: {
                    getZoom: () => {
                        return this.getZoom;
                    },
                    getAspectRatio: () => {
                        return this.getAspectRatio;
                    },
                    setImageDimensions: this.setImageDimensions,
                    vueSet: this.vueSet,
                    vueDelete: this.vueDelete,
                    render: this.render,
                    getCanvasCoordsByMouseEvent:
                        this.getCanvasCoordsByMouseEvent,
                    setZoom: this.setZoom,
                },
            },
            this.maxZoom,
            this.minZoom,
            this.crossBorders
        );

        this.toolManager.registerTool("panZoom", panZoom);

        const TooltipManagerClass = this.tooltipManager.constructor;

        this.tooltipManagerInstance = new TooltipManagerClass({
            $refs: {
                canvas: this.$refs.canvas,
                image: this.$refs.image,
                svgContainer: this.$refs.svgContainer,
            },
            getters: {
                getBreakpoint: () => {
                    return this.getBreakpoint;
                },
                getZoom: () => {
                    return this.getZoom;
                },
                getAspectRatio: () => {
                    return this.getAspectRatio;
                },
            },
            functions: {
                inside: this.inside,
                nextTick: this.$nextTick,
            },
        });
        // window.addEventListener("resize", this.onResize);
        this.loadImage(this.frame);
        // this.toolManager.panZoom.setCenteringValue(this.frame.center);
        // this.toolManager.panZoom.setInitialCentering();
    },

    computed: {
        tooltip() {
            return this.tooltipManagerInstance?.tooltip;
        },
        getNaturalSize() {
            return this.naturalSize;
        },
        getBreakpoint() {
            return this.breakpoint;
        },
        getZoom() {
            return this.zoom;
        },
        getCalculatedWidth() {
            return this.calculatedSize.width;
        },
        getAspectRatio() {
            return this.aspectRatio;
        },
        getCalculatedHeight() {
            return this.calculatedSize.height;
        },
        getInteractiveCanvasData() {
            return {
                toolManager: this.toolManager,
                frameManager: this.frameManager,
                tooltipManager: this.tooltipManagerInstance,
                svg: this.svg,
                $refs: {
                    frame: this.$refs.frame,
                },
                getters: {
                    getBreakpoint: () => {
                        return this.getBreakpoint;
                    },
                    getZoom: () => {
                        return this.getZoom;
                    },
                    getAspectRatio: () => {
                        return this.getAspectRatio;
                    },
                    getNaturalSize: () => {
                        return this.getNaturalSize;
                    },
                },
                functions: {
                    setImageDimensions: this.setImageDimensions,
                    vueSet: this.vueSet,
                    vueDelete: this.vueDelete,
                    render: this.render,
                    getCanvasCoordsByMouseEvent:
                        this.getCanvasCoordsByMouseEvent,
                },
            };
        },
    },
    beforeDestroy() {
        // window.removeEventListener("resize", this.onResize);
    },
    methods: {
        loadImage(frame) {
            if (this.currentImageLoad) {
                this.currentImageLoad.onload = null;
                this.currentImageLoad.onerror = null;
            }
            if (!frame || !frame.image) {
                this.currentImageUrl = null;
                return;
            }
            const defaultImage = new Image();
            this.currentImageLoad = defaultImage;
            defaultImage.src = frame.image.default_url;

            if (defaultImage.complete) {
                // Если дефолт уже загружен, сразу используем его
                this.currentImageUrl = frame.image.default_url;
                this.$emit("defaultImageLoaded");
            } else {
                // Если дефолт еще не загружен, сначала используем превью
                this.currentImageUrl = frame.image.preview_url;
            }

            this.naturalSize.width = frame.image.sizes.original.width;
            this.naturalSize.height = frame.image.sizes.original.height;

            this.$nextTick(async () => {
                await this.setImageDimensions();
                this.toolManager.panZoom.setInitialCentering(this.frame.center);
            });

            if (!defaultImage.complete) {
                // Если дефолт загружается, обработаем его завершение
                defaultImage.onload = () => {
                    this.currentImageUrl = frame.image.default_url;
                    this.$emit("defaultImageLoaded");
                };
                defaultImage.onerror = () => {
                    this.$emit("error", this.getInteractiveCanvasData);
                };
            }
        },

        setZoom(zoom) {
            this.zoom = zoom;
            this.tooltipManagerInstance.handleZoom();
            this.frame.layers.forEach((layer) => {
                if (layer.tool && layer.tool.handleZoom) {
                    layer.tool.handleZoom(layer);
                }
            });
        },

        vueSet(fieldName, layerId, element) {
            this.$set(this[fieldName], layerId, element);
        },
        vueDelete(fieldName, layerId) {
            this.$delete(this[fieldName], layerId);
        },

        handleImageError() {
            this.$emit("error", this.getInteractiveCanvasData);
        },

        handleImageLoaded() {
            // const img = this.$refs.image;
            this.layersShapes = {};

            if (this.svg) {
                this.svg.clear();
                this.svg.viewbox(
                    0,
                    0,
                    this.naturalSize.width,
                    this.naturalSize.height
                );
            } else {
                this.svg = SVGCanvas.addTo(this.$refs.svgContainer)
                    .size("100%", "100%")
                    .viewbox(
                        0,
                        0,
                        this.naturalSize.width,
                        this.naturalSize.height
                    );
            }

            if (this.mounted) {
                this.$emit("loaded", this.getInteractiveCanvasData);
            } else {
                this.mounted = true;
                this.$emit("mounted", this.getInteractiveCanvasData);
            }
        },

        async setImageDimensions() {
            const img = this.$refs.image;
            if (!img) {
                return;
            }
            const aspectRatio =
                this.naturalSize.width / this.naturalSize.height;
            const containerWidth =
                this.defaultSize.width ?? this.$refs.canvas.clientWidth;
            const containerHeight =
                this.defaultSize.height ?? this.$refs.canvas.clientHeight;

            let proportionalH;
            let proportionalW;

            if (this.stretchMode === "height") {
                proportionalH = containerHeight;
                proportionalW =
                    (this.naturalSize.width * containerHeight) /
                    this.naturalSize.height;

                if (
                    proportionalW < containerWidth &&
                    this.naturalSize.width !== this.naturalSize.height
                ) {
                    proportionalW = containerWidth;
                    proportionalH =
                        (this.naturalSize.height * containerWidth) /
                        this.naturalSize.width;
                }
            } else if (this.stretchMode === "width") {
                proportionalW = containerWidth;
                proportionalH = containerWidth / aspectRatio;
            } else if (this.stretchMode === "contain") {
                // Fit image within the container, maintaining aspect ratio
                if (containerWidth / containerHeight > aspectRatio) {
                    proportionalH = containerHeight;
                    proportionalW = containerHeight * aspectRatio;
                } else {
                    proportionalW = containerWidth;
                    proportionalH = containerWidth / aspectRatio;
                }
            } else if (this.stretchMode === "fill") {
                // Fill the container, potentially cropping the image
                if (containerWidth / containerHeight > aspectRatio) {
                    proportionalW = containerWidth;
                    proportionalH = containerWidth / aspectRatio;
                } else {
                    proportionalH = containerHeight;
                    proportionalW = containerHeight * aspectRatio;
                }
            }

            this.calculatedSize.width = proportionalW * this.sizeScale;
            this.calculatedSize.height = proportionalH * this.sizeScale;

            this.aspectRatio =
                this.naturalSize.width / this.calculatedSize.width;
        },

        emitComponent() {
            this.$emit("loaded", this.getInteractiveCanvasData);
        },

        render() {
            this.frameManager.setFrame(this.frame);

            this.frame.layers.forEach((layer) => {
                if (layer.type)
                    if (this.toolManager[layer.type]) {
                        if (layer.tool == null) {
                            layer.tool = this.toolManager[layer.type];
                        }
                        if (layer.state == null) {
                            layer.state = { value: "default" };
                        }
                        this.toolManager[layer.type].render(layer);
                    }
            });
        },

        handleFrameClick(event) {
            this.$emit("frameclick", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerByMouseEvent(event),
            });
        },

        handleContextMenu(event) {
            this.$emit("contextmenu", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerByMouseEvent(event),
            });
        },

        handleWheel(event) {
            this.$emit("wheel", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                // layer: this.getLayerByMouseEvent(event),
            });
        },

        handleMouseOverVue({ event, key }) {
            if (this.hoveredLayer) {
                this.handleLayerMouseLeave(this.hoveredLayer);
            }
            this.hoveredLayer = this.getLayerById(key);
            this.handleLayerMouseOver(this.hoveredLayer);
        },

        handleMouseLeaveVue({ event, key }) {
            if (this.hoveredLayer) {
                this.handleLayerMouseLeave(this.hoveredLayer);
            }

            this.hoveredLayer = this.getLayerById(key);
            this.handleLayerMouseOver(this.hoveredLayer);
        },

        handleMouseMoveVue({ event: e, key }) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
                event.preventDefault();
            } else if (event?.touches?.length === 2) {
                this.pinchZoom(e);
                return;
            }
            this.$emit("mousemove", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerById(key),
            });
        },

        handleMouseDownVue({ event: e, key }) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
            }
            if (event?.touches?.length === 2) {
                return;
            }
            this.$emit("mousedown", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerById(key),
            });
        },

        handleMouseUpVue({ event: e, key }) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
            }

            this.$emit("mouseup", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerById(key),
            });
        },

        handleContextMenuVue({ event, key }) {
            this.$emit("contextmenu", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerById(key),
            });
        },

        handleFrameClickVue({ event, key }) {
            this.$emit("frameclick", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerById(key),
            });
        },

        handleMouseDown(e) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
            }
            if (event?.touches?.length === 2) {
                return;
            }

            this.$emit("mousedown", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerByMouseEvent(event),
            });
        },

        getTargetTouches(touches) {
            return Array.from(touches).map(function (touch) {
                return {
                    x: touch.pageX,
                    y: touch.pageY,
                };
            });
        },

        getDistance(a, b) {
            var x, y;
            x = a.x - b.x;
            y = a.y - b.y;
            return Math.sqrt(x * x + y * y);
        },

        pinchZoom(event) {
            event.preventDefault();
            if (this.startTouches == null) {
                this.startTouches = this.getTargetTouches(event.touches);
                return;
            }
            if (event.touches.length == 2 && this.startTouches.length == 2) {
                const d1 = this.getDistance(
                    this.startTouches[0],
                    this.startTouches[1]
                );

                const endTouches = this.getTargetTouches(event.touches);
                const d2 = this.getDistance(endTouches[0], endTouches[1]);
                event.deltaY = d1 - d2;
                if (event.deltaY === 0) {
                    this.startTouches = null;
                    return;
                }
                event.clientX =
                    (this.startTouches[0].x +
                        this.startTouches[1].x +
                        endTouches[0].x +
                        endTouches[1].x) /
                    4;
                event.clientY =
                    (this.startTouches[0].y +
                        this.startTouches[1].y +
                        endTouches[0].y +
                        endTouches[1].y) /
                    4;

                this.handleWheel(event);
            }
            this.startTouches = null;
        },

        handleMouseMove(e) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
                event.preventDefault();
            } else if (event?.touches?.length === 2) {
                this.pinchZoom(e);
                return;
            }

            this.startTouches = null;

            const layer = this.getLayerByMouseEvent(event);
            this.$emit("mousemove", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer,
            });

            if (layer) {
                if (this.hoveredLayer && layer.id == this.hoveredLayer.id) {
                    return;
                }
                if (this.hoveredLayer && layer.id != this.hoveredLayer.id) {
                    this.handleLayerMouseLeave(this.hoveredLayer);
                }
                this.hoveredLayer = layer;
                this.handleLayerMouseOver(layer);
                return;
            }

            if (this.hoveredLayer) {
                this.handleLayerMouseLeave(this.hoveredLayer);
                this.hoveredLayer = null;
            }
        },

        handleLayerMouseOver(layer) {
            this.$emit("layermouseover", {
                layer,
            });
        },
        handleLayerMouseLeave(layer) {
            this.$emit("layermouseleave", {
                layer,
            });
        },
        handleMouseUp(e) {
            const event = e;
            if (event?.touches?.length === 1) {
                const touch = event.touches[0];
                event.clientX = touch.clientX;
                event.clientY = touch.clientY;
            }
            this.$emit("mouseup", {
                event,
                canvasCoords: this.getCanvasCoordsByMouseEvent(event),
                layer: this.getLayerByMouseEvent(event),
            });
        },

        handleMouseLeave(event) {
            if (this.hoveredLayer) {
                this.handleLayerMouseLeave(this.hoveredLayer);
                this.hoveredLayer = null;
            }
            this.$emit("mouseleave", {
                event,
            });
        },

        getLayerById(id) {
            return this.frameManager.frame.layers.find(
                (layer) => layer.id == id
            );
        },

        getLayerByMouseEvent(e) {
            if (!e || e.offsetX == null) return null;
            if (e.target?.tagName?.toLowerCase() !== "polygon") return null;
            if (!this.frameManager.frame.layers) return null;

            const layer = this.frameManager.frame.layers
                .slice()
                .reverse()
                .find((l) => {
                    return (
                        l.points &&
                        l.type === "polygon" &&
                        this.inside([e.offsetX, e.offsetY], l.points)
                    );
                });

            if (layer) {
                return layer;
            }

            return null;
        },

        inside(point, vs) {
            const [x, y] = point.map((coord) => coord * this.aspectRatio);
            let inside = false;

            for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
                const [xi, yi] = vs[i];
                const [xj, yj] = vs[j];
                const intersect =
                    yi > y !== yj > y &&
                    x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
                if (intersect) inside = !inside;
            }

            return inside;
        },

        getCanvasCoordsByMouseEvent(event) {
            const rect = this.$refs.frame.getBoundingClientRect();
            const scale = this.zoom;
            const x = ((event.clientX - rect.left) / scale) * this.aspectRatio;
            const y = ((event.clientY - rect.top) / scale) * this.aspectRatio;

            return { x, y };
        },
    },
};
</script>

<style></style>
